import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useList } from '@refinedev/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Col, Drawer, Modal, Row, Space, message } from 'antd';
import {
  PageHeader,
  errorMessage,
  errorMessageWithFieldErrors,
  space,
  useCapability,
  useConfirmAction,
} from '@viq-admin/ui';
import { SalesforceConnectionForm } from '@viq-admin/screens';
import type { SalesforceConnectionSaveBody } from '@viq-admin/screens';
import {
  deleteCrmConnection,
  fetchCrmConnections,
  saveCrmConnection,
  testCrmConnection,
} from '../../api/crmConnections';
import { crmOauthStart } from '../../api/crmOauth';
import type { Conference, CrmConnection } from '../../api/types';
import { useCrmCapabilities } from '../../hooks/useCrmCapabilities';
import { navigateExternal } from '../../utils/externalNav';
import {
  DEFAULT_ERROR_MESSAGE,
  ERROR_COPY,
  NOT_ENABLED_COPY,
  notEnabledMessage,
} from './copy';
import { OwnerDefaultsModal } from './OwnerDefaultsModal';
import { ProviderCard } from './ProviderCard';

/** Display labels for the "Owner defaults" modal title, keyed by `crmSource`. */
const CRM_SOURCE_LABEL: Record<string, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  zoho: 'Zoho',
};

/**
 * Portal Integrations page (p4 Task 6 + p5 Task 6 + p6 Task 4). HubSpot (p4)
 * and Zoho (p5) are OAuth Connect/Test/Disconnect flows — started here,
 * finished on the backend, and returned to this same route with
 * `?connected=<source>`. Salesforce (p6) is different: it's a client-
 * credentials connection (consumer key/secret, no OAuth redirect), so its
 * "Connect" is a Set-up/Edit antd `Drawer` hosting the shared
 * `SalesforceConnectionForm` (`packages/screens`, p6 Tasks 2-3 — the same
 * component the super-admin console's create/edit pages inject) rather than
 * a `crmOauthStart` kickoff. Once connected, Salesforce gets the same
 * Test + Disconnect actions HubSpot/Zoho already have — full parity across
 * all three providers, self-serve, no staff involvement (closes ADR 019's
 * G4).
 *
 * The `?error=` codes this page reads on its OAuth return live in `./copy.ts`
 * alongside the rest of this surface's strings. Per-CRM entitlement
 * (migration 044) is a per-card question here: a provider that is off for
 * this company renders greyed with `NOT_ENABLED_COPY`, and its only control
 * is Disconnect when a credential is still stored — the revoke right the
 * backend keeps ungated.
 */

/**
 * A Connect click for a provider whose OAuth app isn't registered yet (the
 * launch checklist deploys this page with hubspotClientId/zohoClientId
 * deliberately blank) hits the backend's `requireEnv`, which throws
 * AdminError.internal('<NAME> env var is not set') — and errorResponse
 * passes INTERNAL messages through verbatim. That env-var name must never
 * reach a tenant's toast, and nothing but the message distinguishes "not
 * configured" from any other internal failure (same status, same code), so
 * the message IS the signal.
 */
const ENV_NOT_SET = /env var is not set/;

/**
 * Display labels for the sources with a live portal OAUTH connect flow.
 * Salesforce isn't here — its self-serve flow (p6) is a Drawer + form, not
 * an OAuth redirect, so it never round-trips through `?connected=<source>`.
 * Doubles as that param's success-toast lookup: a source this build doesn't
 * recognize (a p4 review's "unknown connected= values silently clean the
 * URL" note) still invalidates and cleans the URL, it just skips the toast
 * rather than showing a wrong or blank one.
 */
const CRM_CONNECT_LABEL: Record<string, string> = {
  hubspot: 'HubSpot',
  zoho: 'Zoho',
};

/**
 * Probes a stored connection via POST .../connections/:id/test. Mirrors the
 * console's TestConnectionButton result handling (`{ status: 'ok' | 'error';
 * message }`, not `{ ok }`): 'ok' is a success toast, 'error' and a thrown
 * error both surface as an error modal.
 */
function TestButton({ id }: { id: string }) {
  const [loading, setLoading] = useState(false);

  async function handleTest() {
    setLoading(true);
    try {
      const result = await testCrmConnection(id);
      if (result.status === 'ok') {
        message.success(result.message || 'Connection successful.');
      } else {
        Modal.error({
          title: 'Connection failed',
          content: result.message || 'Unknown error',
        });
      }
    } catch (err: unknown) {
      Modal.error({
        title: 'Connection test error',
        content: errorMessage(err, 'Test failed'),
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button onClick={handleTest} loading={loading}>
      Test
    </Button>
  );
}

export function IntegrationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const confirm = useConfirmAction();
  // Which source's Connect button (if any) is mid-flight. A single flag
  // shared across cards would spin BOTH cards' buttons when only one was
  // clicked — this tracks the one source actually starting.
  const [connectingSource, setConnectingSource] = useState<string | null>(null);
  // Salesforce Set-up/Edit drawer. `null` = closed; 'create'/'edit' drives
  // both which SalesforceConnectionForm mode renders and the drawer's title.
  const [sfDrawerMode, setSfDrawerMode] = useState<'create' | 'edit' | null>(null);
  // Mirrors SalesforceConnectionForm's onDirtyChange — drives the
  // discard-unsaved-changes guard on every way the drawer can close (X, mask,
  // Escape, the drawer's own Cancel button), matching the DetailPage
  // mechanism's copy/behavior the console's ConnectionDetail restored (p6
  // Task 3 fix round) — same guard, same wording, parity across planes.
  const [sfDirty, setSfDirty] = useState(false);
  const [sfSubmitting, setSfSubmitting] = useState(false);
  // Owner-defaults modal (Task 7). `null` = closed; a `crmSource` string
  // opens that provider's modal — one modal instance, reused across
  // providers, rather than three permanently-mounted-but-hidden ones.
  const [ownerDefaultsSource, setOwnerDefaultsSource] = useState<string | null>(null);

  const connectionsQuery = useQuery({
    queryKey: ['crmConnections'],
    queryFn: fetchCrmConnections,
  });
  const { connectedSources, isSourceEnabled } = useCrmCapabilities();
  // manage:CrmIntegration maps 1:1 to company_admin (portalCapabilities.ts),
  // same role the /integrations ROUTE is already restricted to (App.tsx) —
  // so this check is currently always true for anyone who reaches this
  // page. It's still the right gate to add: it's the capability the rest of
  // this page's write actions (and BatchRoute's CRM controls) key off, and
  // it's what stands between these owner-default writes and a future role
  // split that decouples "can view Integrations" from "can manage CRM".
  const canManageCrm = useCapability('manage:CrmIntegration');
  // Conferences for the per-conference override picker. Same resource +
  // hook BatchRoute uses; company_admin (the only role that ever reaches
  // this route) always has read:Conference, so no stakeholder/403 fallback
  // is needed here the way BatchRoute needs one.
  const conferencesQuery = useList<Conference>({ resource: 'conferences' });
  const conferences = conferencesQuery.result?.data ?? [];

  // Read the OAuth-return search params ONCE, off whatever was present at
  // mount — navigate() below clears them, and re-running this on every
  // params change (e.g. a later manual visit with no params) would either
  // re-fire the toast or do nothing, neither useful. Deliberately empty deps.
  //
  // `ranRef` guards against React 18 StrictMode's dev-only double-invoke: with
  // an empty deps array, StrictMode fires this effect twice back-to-back on
  // mount, and both invocations see the SAME initial searchParams — without
  // the guard that's a duplicate toast, a duplicate invalidate, and (mostly
  // harmlessly, since `navigate` is idempotent here) a duplicate replace.
  // Mirrors the repo's own guard idiom (QuestionForm.tsx's `mounted` ref),
  // inverted: that one skips the FIRST run and acts on every run after;
  // this one acts on the FIRST run only and skips every run after.
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const connected = searchParams.get('connected');
    const errorCode = searchParams.get('error');
    if (!connected && !errorCode) return;

    if (connected) {
      const label = CRM_CONNECT_LABEL[connected];
      if (label) message.success(`${label} connected.`);
    } else if (errorCode === 'not_enabled') {
      // The callback names the provider it refused; a backend that predates
      // per-CRM flags sends no `source`, and the generic line covers it.
      const source = searchParams.get('source');
      message.error(notEnabledMessage(source ? CRM_SOURCE_LABEL[source] ?? null : null));
    } else if (errorCode) {
      message.error(ERROR_COPY[errorCode] ?? DEFAULT_ERROR_MESSAGE);
    }
    void queryClient.invalidateQueries({ queryKey: ['crmCapabilities'] });
    navigate('/integrations', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connections = connectionsQuery.data ?? [];
  const salesforceConnection = connections.find((c) => c.crmSource === 'salesforce');
  const hubspotConnection = connections.find((c) => c.crmSource === 'hubspot');
  const zohoConnection = connections.find((c) => c.crmSource === 'zoho');
  const connectionBySource: Record<string, CrmConnection | undefined> = {
    salesforce: salesforceConnection,
    hubspot: hubspotConnection,
    zoho: zohoConnection,
  };
  // The row this modal targets. Falls back to closed if the source's row
  // vanished out from under it (e.g. Disconnect fired while the modal was
  // open) rather than rendering against a stale/undefined connection.
  const ownerDefaultsConnection =
    ownerDefaultsSource != null ? connectionBySource[ownerDefaultsSource] : undefined;
  // "Connected" means the row has CREDENTIALS, not merely that a row exists.
  // Staff can create a Salesforce connection on the console and save the
  // config before the consumer secret is entered, and the backend's
  // /capabilities endpoint (and BatchRoute with it) already gates on the
  // secret — a card reading "Connected" while uploads are refused is the
  // worst of both. `hasSecret` is what the portal's own connections endpoint
  // returns for exactly this question.
  //
  // Either signal still counts: the connections list is the source of truth
  // (and carries the id Test needs), but capabilities is what the rest of the
  // app already relies on — accepting either avoids a flash of
  // "Not connected" while the other request is in flight.
  const salesforceConnected =
    Boolean(salesforceConnection?.hasSecret) || connectedSources.includes('salesforce');
  const hubspotConnected =
    Boolean(hubspotConnection?.hasSecret) || connectedSources.includes('hubspot');
  const zohoConnected =
    Boolean(zohoConnection?.hasSecret) || connectedSources.includes('zoho');
  const salesforceEnabled = isSourceEnabled('salesforce');
  const hubspotEnabled = isSourceEnabled('hubspot');
  const zohoEnabled = isSourceEnabled('zoho');

  function handleConnect(source: string) {
    const label = CRM_CONNECT_LABEL[source] ?? source;
    setConnectingSource(source);
    crmOauthStart(source)
      .then(({ url }) => {
        navigateExternal(url);
      })
      .catch((err: unknown) => {
        setConnectingSource(null);
        message.error(
          ENV_NOT_SET.test(errorMessage(err, ''))
            ? `${label} isn’t available yet — contact VendoIQ support.`
            : errorMessage(err, `Could not start the ${label} connection.`),
        );
      });
  }

  function handleDisconnect(label: string, connectionId: string, content?: string) {
    confirm({
      title: `Disconnect ${label}?`,
      content: content ?? `Leads will no longer upload to ${label}.`,
      okText: 'Disconnect',
      danger: true,
      successMessage: `${label} disconnected.`,
      onConfirm: async () => {
        await deleteCrmConnection(connectionId);
        await queryClient.invalidateQueries({ queryKey: ['crmConnections'] });
        await queryClient.invalidateQueries({ queryKey: ['crmCapabilities'] });
      },
    });
  }

  /**
   * The greyed card's copy: why it's off, and — only when a row exists —
   * that something is still stored here.
   */
  function notEnabledDescription(label: string, row: CrmConnection | undefined): ReactNode {
    // Two block-level text nodes, not one string with a <br />: each sentence
    // is its own node so the page tests can match either exactly, and no
    // <div> lands inside ProviderCard's <Typography.Text> span.
    return (
      <>
        <span style={{ display: 'block' }}>{NOT_ENABLED_COPY.body(label)}</span>
        {row && (
          <span style={{ display: 'block', marginTop: space.xs }}>
            {NOT_ENABLED_COPY.stillStored(label)}
          </span>
        )}
      </>
    );
  }

  /**
   * The greyed card's ONLY control. Connect / Set up / Edit / Test / Owner
   * defaults would each be a button that can only 403; Disconnect is the one
   * thing the backend keeps open to a company whose provider is off.
   */
  function notEnabledActions(label: string, row: CrmConnection | undefined): ReactNode {
    if (!row) return null;
    return (
      <Button
        danger
        onClick={() => handleDisconnect(label, row.id, NOT_ENABLED_COPY.disconnectConfirm(label))}
      >
        Disconnect
      </Button>
    );
  }

  // Closes the drawer unconditionally — the caller has already decided this
  // is safe (either nothing was dirty, or the discard was confirmed).
  function closeSfDrawer() {
    setSfDrawerMode(null);
    setSfDirty(false);
  }

  // The one handler behind every way the drawer can close: the X icon, a
  // mask click, Escape, and the drawer's own Cancel button all route through
  // `onClose`/`onClick={requestCloseSfDrawer}` below. A clean form closes
  // silently; a dirty one gets the exact DetailPage discard-guard copy
  // (packages/ui/src/DetailPage.tsx) so the wording matches the console's
  // ConnectionDetail guard — the same guard, restored across both planes.
  function requestCloseSfDrawer() {
    if (!sfDirty) {
      closeSfDrawer();
      return;
    }
    confirm({
      title: 'Discard unsaved changes?',
      content: 'Your edits to this record will be lost.',
      okText: 'Discard',
      danger: true,
      onConfirm: async () => closeSfDrawer(),
    });
  }

  async function handleSfSubmit(body: SalesforceConnectionSaveBody) {
    setSfSubmitting(true);
    try {
      if (sfDrawerMode === 'edit') {
        if (!salesforceConnection) throw new Error('Salesforce connection not found.');
        await saveCrmConnection('edit', salesforceConnection.id, body);
        message.success('Salesforce connection saved.');
      } else {
        await saveCrmConnection('create', undefined, body);
        message.success('Salesforce connected.');
      }
      await queryClient.invalidateQueries({ queryKey: ['crmConnections'] });
      await queryClient.invalidateQueries({ queryKey: ['crmCapabilities'] });
      closeSfDrawer();
    } catch (err: unknown) {
      // errorMessageWithFieldErrors, not errorMessage: a 422 that slips past
      // the form's client rules arrives as a bare 'Validation failed' whose
      // actionable part lives in the fieldErrors map adminFetch put on
      // `errors` — append it so the tenant learns which rule failed.
      message.error(errorMessageWithFieldErrors(err, 'Failed to save the Salesforce connection.'));
      // Re-throw so SalesforceConnectionForm's own try/catch sees the
      // rejection and skips its onDirtyChange(false) reset — a failed save
      // must keep the discard guard armed on the operator's still-unsaved
      // edits. The form swallows this itself, so it never surfaces as an
      // unhandled rejection here.
      throw err;
    } finally {
      setSfSubmitting(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Connect your CRM to send captured leads automatically."
      />
      {connectionsQuery.isError ? (
        // The capability probe failing closed is by design — it gates the
        // Batch affordance — but this page is the tenant's view of what IS
        // connected. Rendering the no-row branches off a failed list fetch
        // shows a connected Salesforce as "Not connected" with a live Set up
        // whose submit can only 409 ("edit it instead" — with no Edit button
        // on screen to do it). Say "don't know" and offer a retry instead.
        <Alert
          type="error"
          showIcon
          message="Couldn’t load your integrations."
          description="Your connections can’t be shown right now — nothing has been disconnected."
          action={
            <Button size="small" onClick={() => void connectionsQuery.refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <Row gutter={[space.lg, space.lg]}>
          <Col xs={24} md={12} xl={8}>
            <ProviderCard
              title="Salesforce"
              enabled={salesforceEnabled}
              connected={salesforceConnected}
              description={
                salesforceEnabled
                  ? 'Connect your Salesforce org with its own client credentials.'
                  : notEnabledDescription('Salesforce', salesforceConnection)
              }
              // Actions key off the ROW, not `salesforceConnected` — same
              // reasoning as HubSpot/Zoho: Edit/Test/Disconnect all need the
              // row's id, and a credential-less row (staff-created, secret not
              // yet entered) is exactly the one a tenant most needs to reach
              // Edit or Disconnect on.
              actions={
                !salesforceEnabled ? (
                  notEnabledActions('Salesforce', salesforceConnection)
                ) : salesforceConnection ? (
                  <Space wrap>
                    <Button onClick={() => setSfDrawerMode('edit')}>Edit</Button>
                    <TestButton id={salesforceConnection.id} />
                    {canManageCrm && (
                      <Button onClick={() => setOwnerDefaultsSource('salesforce')}>
                        Owner defaults
                      </Button>
                    )}
                    <Button
                      danger
                      onClick={() => handleDisconnect('Salesforce', salesforceConnection.id)}
                    >
                      Disconnect
                    </Button>
                  </Space>
                ) : (
                  <Button type="primary" onClick={() => setSfDrawerMode('create')}>
                    Set up
                  </Button>
                )
              }
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <ProviderCard
              title="HubSpot"
              enabled={hubspotEnabled}
              connected={hubspotConnected}
              description={
                hubspotEnabled
                  ? 'Send captured leads straight to HubSpot as they come in.'
                  : notEnabledDescription('HubSpot', hubspotConnection)
              }
              // Actions key off the ROW, not `hubspotConnected`: Test and
              // Disconnect both need the row's id, and a credential-less row is
              // exactly the one a tenant most needs to be able to clear.
              actions={
                !hubspotEnabled ? (
                  notEnabledActions('HubSpot', hubspotConnection)
                ) : hubspotConnection ? (
                  <Space wrap>
                    <TestButton id={hubspotConnection.id} />
                    {canManageCrm && (
                      <Button onClick={() => setOwnerDefaultsSource('hubspot')}>
                        Owner defaults
                      </Button>
                    )}
                    <Button
                      danger
                      onClick={() => handleDisconnect('HubSpot', hubspotConnection.id)}
                    >
                      Disconnect
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    loading={connectingSource === 'hubspot'}
                    onClick={() => handleConnect('hubspot')}
                  >
                    Connect
                  </Button>
                )
              }
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <ProviderCard
              title="Zoho"
              enabled={zohoEnabled}
              connected={zohoConnected}
              description={
                zohoEnabled
                  ? "Zoho CRM — works across Zoho’s regional data centers automatically."
                  : notEnabledDescription('Zoho', zohoConnection)
              }
              // Actions key off the ROW, not `zohoConnected` — same reasoning
              // as HubSpot: Test and Disconnect need the row's id, and a
              // credential-less row is exactly the one a tenant most needs to
              // be able to clear.
              actions={
                !zohoEnabled ? (
                  notEnabledActions('Zoho', zohoConnection)
                ) : zohoConnection ? (
                  <Space wrap>
                    <TestButton id={zohoConnection.id} />
                    {canManageCrm && (
                      <Button onClick={() => setOwnerDefaultsSource('zoho')}>
                        Owner defaults
                      </Button>
                    )}
                    <Button danger onClick={() => handleDisconnect('Zoho', zohoConnection.id)}>
                      Disconnect
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    loading={connectingSource === 'zoho'}
                    onClick={() => handleConnect('zoho')}
                  >
                    Connect
                  </Button>
                )
              }
            />
          </Col>
        </Row>
      )}
      <Drawer
        title={sfDrawerMode === 'edit' ? 'Edit Salesforce connection' : 'Connect Salesforce'}
        open={sfDrawerMode !== null}
        onClose={requestCloseSfDrawer}
        destroyOnClose
        width={480}
        extra={<Button onClick={requestCloseSfDrawer}>Cancel</Button>}
      >
        {sfDrawerMode && (
          <SalesforceConnectionForm
            mode={sfDrawerMode}
            initial={sfDrawerMode === 'edit' ? salesforceConnection : undefined}
            onSubmit={handleSfSubmit}
            submitting={sfSubmitting}
            onDirtyChange={setSfDirty}
          />
        )}
      </Drawer>
      {ownerDefaultsSource && ownerDefaultsConnection && (
        <OwnerDefaultsModal
          open
          onClose={() => setOwnerDefaultsSource(null)}
          source={ownerDefaultsSource}
          sourceLabel={CRM_SOURCE_LABEL[ownerDefaultsSource] ?? ownerDefaultsSource}
          connection={ownerDefaultsConnection}
          conferences={conferences}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: ['crmConnections'] })}
        />
      )}
    </div>
  );
}
